import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ALLERGENS } from "../lib/menuConstants";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Printer, FloppyDisk, Warning } from "@phosphor-icons/react";
import { openPrintWindow } from "../lib/printWindow";
import { SortHeader } from "../components/SortHeader";
import { useTableSort } from "../lib/useTableSort";

const ALL = "__all__";

export default function AdminAllergens() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCat, setSelectedCat] = useState(ALL);
  const [pending, setPending] = useState({}); // { [itemId]: allergens[] }
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printCats, setPrintCats] = useState({});
  const [barName, setBarName] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/menu-items"),
      api.get("/settings/bar"),
    ])
      .then(([c, m, b]) => {
        setCategories(c.data);
        setItems(m.data);
        setBarName(b.data.bar_name || "");
        setPrintCats(Object.fromEntries(c.data.filter((x) => !x.parent_id).map((x) => [x.id, true])));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const topLevels = useMemo(
    () => categories.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order),
    [categories]
  );
  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const parentId = (id) => categories.find((c) => c.id === id)?.parent_id || id;

  const itemsInScope = useMemo(() => {
    if (selectedCat === ALL) return items;
    return items.filter((it) => parentId(it.category_id) === selectedCat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedCat, categories]);
  const itemsWithCat = useMemo(
    () => itemsInScope.map((it) => ({ ...it, cat_name: catName(it.category_id) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsInScope, categories],
  );
  const algSort = useTableSort(itemsWithCat, null);

  const currentAllergens = (item) =>
    pending[item.id] !== undefined ? pending[item.id] : item.allergens || [];

  const toggle = (item, allergenId) => {
    const current = currentAllergens(item);
    const next = current.includes(allergenId)
      ? current.filter((a) => a !== allergenId)
      : [...current, allergenId];
    setPending((p) => ({ ...p, [item.id]: next }));
  };

  const toggleAllForItem = (item, checkAll) => {
    setPending((p) => ({ ...p, [item.id]: checkAll ? ALLERGENS.map((a) => a.id) : [] }));
  };

  const dirtyCount = Object.keys(pending).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      // Save each modified item
      for (const itemId of Object.keys(pending)) {
        const it = items.find((x) => x.id === itemId);
        if (!it) continue;
        const payload = { ...it, allergens: pending[itemId] };
        // Remove server-managed fields
        delete payload.id;
        delete payload.translations;
        delete payload._id;
        await api.put(`/menu-items/${itemId}`, payload);
      }
      toast.success(`${dirtyCount} artículo(s) actualizados`);
      // Refresh
      const m = await api.get("/menu-items");
      setItems(m.data);
      setPending({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doPrint = () => {
    const selectedTop = topLevels.filter((c) => printCats[c.id]);
    const sections = selectedTop
      .map((top) => {
        const kids = categories.filter((c) => c.parent_id === top.id);
        const catIds = [top.id, ...kids.map((k) => k.id)];
        const rows = items
          .filter((it) => catIds.includes(it.category_id))
          .filter((it) => (it.allergens || []).length > 0)
          .sort((a, b) => a.name.localeCompare(b.name));
        return { top, rows };
      })
      .filter((s) => s.rows.length > 0);

    if (!sections.length) {
      toast.error("Ninguna categoría seleccionada tiene artículos con alérgenos");
      return;
    }

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Alérgenos</title>
    <style>
      @page { size: A4; margin: 15mm 12mm 28mm 12mm; }
      body { font-family: -apple-system, "Segoe UI", sans-serif; font-size: 11px; color: #111; margin: 0; }
      .membrete { text-align:center; font-size: 16px; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; padding-bottom: 6px; border-bottom: 2px solid #111; margin-bottom: 12px; }
      .print-date { text-align: right; font-size: 9px; color: #666; margin-top: -22px; margin-bottom: 18px; }
      h2 { font-size: 18px; margin: 22px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { padding: 5px 4px; border-bottom: 1px solid #e6e6e6; text-align: left; vertical-align: top; }
      th { font-size: 8px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; }
      td.item { font-weight: 600; }
      .allergens { font-size: 12px; letter-spacing: 2px; }
      .note { font-size: 9px; color: #888; margin-top: 10px; text-align: center; }
      .page-footer {
        position: fixed;
        bottom: 4mm;
        left: 12mm;
        right: 12mm;
        font-size: 9px;
        font-style: italic;
        color: #555;
        text-align: center;
        border-top: 1px solid #ccc;
        padding-top: 3mm;
        line-height: 1.4;
      }
      .page-footer p { margin: 2px 0; }
      @media print {
        .page-footer { position: fixed; bottom: 0; }
      }
    </style></head><body>
    <div class="membrete">${escape(barName || "Alérgenos")}</div>
    <div class="print-date">Alérgenos · ${new Date().toLocaleDateString("es-ES")}</div>
    ${sections.map(sec => `
      <h2>${escape(sec.top.name)}</h2>
      <table>
        <thead>
          <tr>
            <th style="width: 45%">Artículo</th>
            <th style="width: 25%">Alérgenos</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          ${sec.rows.map(it => {
            const list = (it.allergens || []).map(id => ALLERGENS.find(a=>a.id===id)).filter(Boolean);
            return `<tr>
              <td class="item">${escape(it.name)}</td>
              <td class="allergens">${list.map(a=>a.icon).join(" ")}</td>
              <td>${list.map(a=>escape(a.label)).join(" · ")}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    `).join("")}
    <div class="note">Información sobre alérgenos según reglamento UE 1169/2011.</div>
    <div class="page-footer">
      <p>IMPORTANT: Com que tots els nostres productes són elaborats de forma casolana i artesanal, poden contindre traces de qualsevol al·lergen.</p>
      <p>AVÍS: Les fotografies són merament orientatives i algunes han sigut generades mitjançant IA, per la qual cosa poden no coincidir exactament amb el producte final.</p>
    </div>
    </body></html>`;

    const w = openPrintWindow(html, { features: "width=900,height=900" });
    if (!w) { toast.error("Permite ventanas emergentes"); return; }
    setPrintOpen(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="label-cap">carta digital</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Alérgenos</h1>
          <p className="text-[var(--text-secondary)] text-sm mt-1">
            Marca los alérgenos por artículo. Los cambios se aplican también a la carta pública.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-64">
            <Select value={selectedCat} onValueChange={setSelectedCat}>
              <SelectTrigger data-testid="allergen-cat-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas las categorías</SelectItem>
                {topLevels.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => setPrintOpen(true)}
            data-testid="print-allergens-btn"
          >
            <Printer size={16} className="mr-2" /> Imprimir
          </Button>
          <Button
            className="btn-primary"
            onClick={save}
            disabled={!dirtyCount || saving}
            data-testid="save-allergens-btn"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? "Guardando…" : `Guardar${dirtyCount ? ` (${dirtyCount})` : ""}`}
          </Button>
        </div>
      </div>

      <div className="card-flat overflow-auto max-h-[70vh]" data-testid="allergens-table-container">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-[var(--border)] bg-[var(--bg)] shadow-sm">
              <th className="text-left p-3 sticky left-0 top-0 z-30 bg-[var(--bg)] min-w-[220px]"><SortHeader sortKey="name" currentKey={algSort.sortKey} currentDir={algSort.sortDir} onClick={algSort.toggleSort}>Artículo</SortHeader></th>
              <th className="text-left p-3 min-w-[140px] bg-[var(--bg)]"><SortHeader sortKey="cat_name" currentKey={algSort.sortKey} currentDir={algSort.sortDir} onClick={algSort.toggleSort}>Categoría</SortHeader></th>
              {ALLERGENS.map((a) => (
                <th key={a.id} className="p-3 label-cap text-center whitespace-nowrap bg-[var(--bg)]" title={a.label}>
                  <div className="text-lg leading-none">{a.icon}</div>
                  <div className="text-[9px] mt-1">{a.label}</div>
                </th>
              ))}
              <th className="p-3 label-cap text-center min-w-[70px] bg-[var(--bg)]">Todo</th>
            </tr>
          </thead>
          <tbody>
            {algSort.sortedRows.map((it) => {
              const cur = currentAllergens(it);
              const dirty = pending[it.id] !== undefined;
              const allChecked = cur.length === ALLERGENS.length;
              return (
                <tr
                  key={it.id}
                  className={`border-b border-[var(--border)] last:border-0 ${dirty ? "bg-yellow-50" : "hover:bg-[var(--bg)]"}`}
                  data-testid={`allergen-row-${it.id}`}
                >
                  <td className="p-3 font-display font-semibold sticky left-0 bg-inherit">
                    {dirty && <Warning size={12} weight="fill" className="inline text-[var(--primary)] mr-1" />}
                    {it.name}
                  </td>
                  <td className="p-3 text-[var(--text-secondary)] text-xs">{catName(it.category_id)}</td>
                  {ALLERGENS.map((a) => (
                    <td key={a.id} className="p-3 text-center">
                      <Checkbox
                        checked={cur.includes(a.id)}
                        onCheckedChange={() => toggle(it, a.id)}
                        data-testid={`chk-${it.id}-${a.id}`}
                      />
                    </td>
                  ))}
                  <td className="p-3 text-center">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={(v) => toggleAllForItem(it, !!v)}
                      data-testid={`chk-all-${it.id}`}
                    />
                  </td>
                </tr>
              );
            })}
            {itemsInScope.length === 0 && (
              <tr>
                <td colSpan={ALLERGENS.length + 3} className="p-10 text-center text-[var(--text-secondary)]">
                  Sin artículos en esta categoría.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {printOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-6" data-testid="print-dialog">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Imprimir alérgenos</h2>
            <p className="text-[var(--text-secondary)] text-sm mb-5">
              Selecciona las familias. Solo se imprimen los artículos con al menos un alérgeno.
            </p>
            <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto pr-2">
              {topLevels.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 p-2 rounded-lg border border-[var(--border)] cursor-pointer hover:bg-[var(--bg)]"
                >
                  <Checkbox
                    checked={!!printCats[c.id]}
                    onCheckedChange={(v) => setPrintCats((p) => ({ ...p, [c.id]: !!v }))}
                    data-testid={`print-cat-${c.id}`}
                  />
                  <span className="font-display font-semibold">{c.name}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPrintCats(Object.fromEntries(topLevels.map((c) => [c.id, true])))}
                >
                  Todas
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPrintCats(Object.fromEntries(topLevels.map((c) => [c.id, false])))}
                >
                  Ninguna
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setPrintOpen(false)}>Cancelar</Button>
                <Button
                  className="btn-primary"
                  onClick={doPrint}
                  data-testid="print-confirm-btn"
                >
                  <Printer size={16} className="mr-2" /> Imprimir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function escape(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
